"use client";

import {
  Box,
  Button,
  Field,
  Flex,
  HStack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import type { ConfirmThoughtsRequest, MeiliThought } from "@/generated/fastapi";
import {
  type MeiliDocumentSearchHit,
  MEILI_SEARCH_CONFIG,
} from "@/features/search";
import appSettings from "@/shared/config/appSettings";
import { useSystemDesign } from "@/shared/system-design";
import { CONTENT_ITEM_FOCUS_VISIBLE_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import { CONTENT_ITEM_HOVER_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import ContentItemCard from "@/shared/ui/display/ContentItemCard";
import { ContentItemMenuLabel } from "@/shared/ui/display/ContentItemPrimitives";
import { ContentItemPreviewActions } from "@/shared/ui/display/ContentItemPrimitives";
import { ContentItemUnassignedCallout } from "@/shared/ui/display/ContentItemPrimitives";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import WithMenu from "@/shared/ui/actions/WithMenu";
import WithMenuItem from "@/shared/ui/actions/WithMenuItem";
import WithSwipeSelector from "@/shared/ui/gestures/WithSwipeSelector";
import useThoughtItemActions, {
  type ThoughtItemAction,
} from "../hooks/useThoughtItemActions";
import useThoughtActions from "../hooks/useThoughtActions";
import useThoughtItemGestures from "../hooks/useThoughtItemGestures";
import ThoughtItemPreview from "./ThoughtItemPreview";

type ThoughtItemSelection = {
  isSelected: boolean;
  onToggle: () => void;
  humanReadable: string;
};

type ThoughtItemProps = {
  config: (typeof MEILI_SEARCH_CONFIG)[keyof typeof MEILI_SEARCH_CONFIG];
  hit: MeiliDocumentSearchHit;
  isInSearchContext?: boolean;
  onDeleteSearchResultAction?: (thoughtId: string) => void;
  onUpdateSearchResultAction?: (updatedThought: MeiliThought) => void;
  onThoughtUpdateSuccess?: () => void;
  resetReflections?: boolean;
  selection?: ThoughtItemSelection;
  anchorAction?: ThoughtItemAction;
  primaryAction?: ThoughtItemAction;
  onReflectAction?: (thought: MeiliThought) => void | Promise<void>;
  onTraceOriginAction?: (thought: MeiliThought) => void | Promise<void>;
};

const THOUGHT_ITEM_ACTIONS_OPEN_EVENT = "moveon:thought-item-actions-open";
const DESKTOP_INLINE_ACTION_KEYS = new Set([
  "reflect",
  "anchor",
  "trace_origin",
]);

function getDraftValidationMessage(draftMindstring: string) {
  const trimmedDraft = draftMindstring.trim();

  if (trimmedDraft.length === 0) {
    return "Add thought text before saving.";
  }

  if (trimmedDraft.length < appSettings.validation.MINSTRING_MIN_LENGTH) {
    return `Thought is too short. Minimum length is ${appSettings.validation.MINSTRING_MIN_LENGTH} characters.`;
  }

  if (trimmedDraft.length > appSettings.validation.MINSTRING_MAX_LENGTH) {
    return `Thought is too long. Maximum length is ${appSettings.validation.MINSTRING_MAX_LENGTH} characters.`;
  }

  return null;
}

function ThoughtItem({
  config,
  hit,
  isInSearchContext = true,
  onDeleteSearchResultAction,
  onUpdateSearchResultAction,
  onThoughtUpdateSuccess,
  resetReflections = true,
  selection,
  anchorAction,
  primaryAction,
  onReflectAction,
  onTraceOriginAction,
}: ThoughtItemProps) {
  const { isMobileDevice } = useSystemDesign();
  const { handleThoughtUpdateClick } = useThoughtActions();
  const thought = hit as MeiliThought;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isSelected = selection?.isSelected ?? false;
  const backgroundColor = "moveon.surface.bg";
  const isUnassigned = !thought.situation_id;
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftMindstring, setDraftMindstring] = useState(thought.mindstring);
  const suppressCardActionRef = useRef(false);
  const suppressCardActionTimeoutRef =
    useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftValidationMessage = getDraftValidationMessage(draftMindstring);
  const hasDraftChanges = draftMindstring !== thought.mindstring;
  const saveDraftDisabledReason = draftValidationMessage
    ? draftValidationMessage
    : !hasDraftChanges
      ? "Make a change before saving."
      : null;
  const handleStartEdit = useCallback(() => {
    setDraftMindstring(thought.mindstring);
    setIsEditing(true);
    setIsMobileActionsOpen(isMobileDevice);
  }, [isMobileDevice, thought.mindstring]);
  const resolvedActions = useThoughtItemActions({
    anchorAction,
    isEditing,
    onDeleteSearchResultAction,
    onEditAction: handleStartEdit,
    onReflectAction,
    onTraceOriginAction:
      thought.origin_id && onTraceOriginAction
        ? () => onTraceOriginAction(thought)
        : undefined,
    thought,
    thoughtText: thought.mindstring,
  });
  const desktopInlineActions = resolvedActions.filter((action) =>
    DESKTOP_INLINE_ACTION_KEYS.has(action.key),
  );
  const desktopOverflowActions = resolvedActions.filter(
    (action) => !DESKTOP_INLINE_ACTION_KEYS.has(action.key),
  );
  const handleDesktopOverflowOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (suppressCardActionTimeoutRef.current) {
        clearTimeout(suppressCardActionTimeoutRef.current);
        suppressCardActionTimeoutRef.current = null;
      }

      if (details.open) {
        suppressCardActionRef.current = true;
        return;
      }

      suppressCardActionTimeoutRef.current = setTimeout(() => {
        suppressCardActionRef.current = false;
        suppressCardActionTimeoutRef.current = null;
      }, 350);
    },
    [],
  );
  const canActivateCard =
    !isEditing &&
    Boolean(onReflectAction || (isMobileDevice && resolvedActions.length > 0));
  const handleCardActivate = useCallback(() => {
    if (isEditing) {
      return;
    }

    if (suppressCardActionRef.current) {
      return;
    }

    if (isMobileDevice && resolvedActions.length > 0) {
      const nextIsOpen = !isMobileActionsOpen;

      if (nextIsOpen) {
        window.dispatchEvent(
          new CustomEvent(THOUGHT_ITEM_ACTIONS_OPEN_EVENT, {
            detail: { thoughtId: thought.objectID },
          }),
        );
      }

      setIsMobileActionsOpen(nextIsOpen);
      return;
    }

    if (onReflectAction) {
      void onReflectAction(thought);
      return;
    }
  }, [
    isEditing,
    isMobileActionsOpen,
    isMobileDevice,
    onReflectAction,
    resolvedActions.length,
    thought,
  ]);
  const {
    handleCardClick,
    handleCardKeyDown,
    handlePointerDown,
    handlePointerMove,
  } = useThoughtItemGestures({
    canOpenDetail: canActivateCard,
    onOpenDetail: handleCardActivate,
  });

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const focusHandle = requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.focus();
      const cursorPosition = textarea.value.length;
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    });

    return () => cancelAnimationFrame(focusHandle);
  }, [isEditing]);

  const handleCancelEdit = useCallback(() => {
    setDraftMindstring(thought.mindstring);
    setIsEditing(false);
    setIsMobileActionsOpen(isMobileDevice);
  }, [isMobileDevice, thought.mindstring]);

  const handleSaveDraft = useCallback(async () => {
    if (saveDraftDisabledReason) {
      return;
    }

    const updatedThought: MeiliThought = {
      ...thought,
      mindstring: draftMindstring,
    };
    const request: ConfirmThoughtsRequest = {
      thought_ids: [thought.objectID],
      edited_mindstring: draftMindstring,
      reset_reflections: resetReflections,
    };

    await handleThoughtUpdateClick({
      request,
      updatedThought,
      onSuccess: (savedThought) => {
        setIsEditing(false);
        setDraftMindstring(savedThought.mindstring);
        onUpdateSearchResultAction?.(savedThought);
        onThoughtUpdateSuccess?.();
      },
      affectedPrototypes: [thought.prototype],
    });
  }, [
    draftMindstring,
    handleThoughtUpdateClick,
    onThoughtUpdateSuccess,
    onUpdateSearchResultAction,
    resetReflections,
    saveDraftDisabledReason,
    thought,
  ]);

  useEffect(() => {
    if (!isMobileActionsOpen) {
      return;
    }

    const handleAnotherThoughtActionsOpen = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }

      if (event.detail?.thoughtId !== thought.objectID) {
        setIsMobileActionsOpen(false);
      }
    };

    window.addEventListener(
      THOUGHT_ITEM_ACTIONS_OPEN_EVENT,
      handleAnotherThoughtActionsOpen,
    );

    return () => {
      window.removeEventListener(
        THOUGHT_ITEM_ACTIONS_OPEN_EVENT,
        handleAnotherThoughtActionsOpen,
      );
    };
  }, [isMobileActionsOpen, thought.objectID]);

  useEffect(() => {
    return () => {
      if (suppressCardActionTimeoutRef.current) {
        clearTimeout(suppressCardActionTimeoutRef.current);
      }
    };
  }, []);

  const renderActionMenuLabel = (action: (typeof resolvedActions)[number]) => (
    <ContentItemMenuLabel
      label={action.label}
      icon={action.icon}
      color={
        action.disabled
          ? "moveon.text.muted"
          : action.tone === "danger"
            ? "moveon.danger"
            : action.color
      }
    />
  );

  const previewContent = (
    <Flex align="start" gap="3" width="full">
      <Box
        flex="1"
        minW={0}
      >
        {isUnassigned && (
          <ContentItemUnassignedCallout
            mb="2"
            // message="Confirmed thought without assigned situation"
          />
        )}
        <ThoughtItemPreview
          config={config}
          hit={hit}
          isInSearchContext={isInSearchContext}
          thought={thought}
        />
      </Box>

      <Box marginInlineStart="auto">
        <Box
          display={{ base: "block", lg: "none" }}
          color="moveon.text.muted"
        >
          <GenericIcon
            icon="scroll_down"
            humanReadable={
              isMobileActionsOpen
                ? "Hide confirmed thought actions"
                : "Show confirmed thought actions"
            }
          />
        </Box>
        <Box
          display={{ base: "none", lg: "block" }}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <HStack gap="2" align="center">
            {desktopOverflowActions.length > 0 ? (
              <WithMenu
                size="sm"
                placement="bottom-end"
                onOpenChange={handleDesktopOverflowOpenChange}
              >
                {desktopOverflowActions.map((action) => (
                  <WithMenuItem
                    key={action.key}
                    value={action.key}
                    humanReadable={action.humanReadable}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    tone={action.tone === "danger" ? "error" : undefined}
                  >
                    {renderActionMenuLabel(action)}
                  </WithMenuItem>
                ))}
              </WithMenu>
            ) : null}
            <ContentItemPreviewActions actions={desktopInlineActions} />
          </HStack>
        </Box>
      </Box>
    </Flex>
  );

  const card = (
    <ContentItemCard
      role={canActivateCard ? "button" : undefined}
      tabIndex={canActivateCard ? 0 : undefined}
      selected={isSelected}
      bg="moveon.surface.bg"
      selectedBg="moveon.surface.bg"
      selectedBorderColor="moveon.secondary"
      selectedOutlineWidth="0"
      borderWidth="1"
      accentColor={isUnassigned ? "moveon.warning" : undefined}
      cursor={canActivateCard ? "pointer" : undefined}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      _hover={CONTENT_ITEM_HOVER_STYLES}
      _focusVisible={
        canActivateCard ? CONTENT_ITEM_FOCUS_VISIBLE_STYLES : undefined
      }
    >
      <VStack align="stretch" gap="3">
        {previewContent}
        {isMobileActionsOpen && !isEditing ? (
          <Flex
            display={{ base: "flex", lg: "none" }}
            gap="2"
            wrap="nowrap"
            width="full"
            justify="flex-end"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {[...resolvedActions].reverse().map((action) => (
              <Button
                key={action.key}
                type="button"
                size="sm"
                variant={action.emphasized ? "solid" : "outline"}
                bg={action.emphasized ? "moveon.primary" : undefined}
                color={
                  action.emphasized
                    ? "white"
                    : action.tone === "danger"
                      ? "moveon.danger"
                      : action.color
                }
                borderColor={
                  action.tone === "danger"
                    ? "moveon.dangerBorder"
                    : "moveon.surface.border"
                }
                flex="0 0 auto"
                minW="10"
                width="10"
                height="10"
                px="0"
                disabled={action.disabled}
                onClick={() => {
                  setIsMobileActionsOpen(false);
                  void action.onClick();
                }}
                aria-label={action.humanReadable}
                title={action.humanReadable}
              >
                <GenericIcon icon={action.icon} />
              </Button>
            ))}
          </Flex>
        ) : null}
        {isEditing ? (
          <Field.Root
            invalid={Boolean(draftValidationMessage)}
            width="full"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <VStack align="stretch" gap="3" width="full">
              <Text fontSize="xs" color="moveon.text.muted">
                {saveDraftDisabledReason ?? "Save this confirmed thought."}
              </Text>
              <Textarea
                ref={textareaRef}
                value={draftMindstring}
                onChange={(event) => {
                  setDraftMindstring(event.target.value);
                }}
                autoresize
                width="full"
                maxW="none"
                minH="20"
                color="moveon.surface.fg"
                bg="moveon.surface.elevatedBg"
                borderColor="moveon.surface.border"
                aria-label="Confirmed thought text"
                onKeyDown={(event) => {
                  if (
                    (event.metaKey || event.ctrlKey) &&
                    event.key === "Enter"
                  ) {
                    event.preventDefault();
                    void handleSaveDraft();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    handleCancelEdit();
                  }
                }}
              />
              {draftValidationMessage ? (
                <Field.ErrorText>{draftValidationMessage}</Field.ErrorText>
              ) : null}
              <Flex justify="flex-end" gap="2" wrap="wrap">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  aria-label="Cancel editing confirmed thought"
                >
                  <GenericIcon icon="return" />
                  <Text as="span" display={{ base: "none", sm: "inline" }}>
                    Cancel
                  </Text>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  bg="moveon.primary"
                  color="white"
                  disabled={Boolean(saveDraftDisabledReason)}
                  onClick={() => {
                    void handleSaveDraft();
                  }}
                >
                  <GenericIcon icon="save" />
                  <Text as="span" display={{ base: "none", sm: "inline" }}>
                    Save
                  </Text>
                </Button>
              </Flex>
            </VStack>
          </Field.Root>
        ) : null}
      </VStack>
    </ContentItemCard>
  );

  const selectableCard =
    !selection && !primaryAction ? (
      card
    ) : (
      <WithSwipeSelector
        isMobile={isMobileDevice}
        selection={
          selection
            ? {
                isSelected: selection.isSelected,
                onToggle: selection.onToggle,
                humanReadable: selection.humanReadable,
              }
            : undefined
        }
        action={
          primaryAction
            ? {
                onSwipe: () => primaryAction.onClick(thought),
                icon: primaryAction.icon,
                color: primaryAction.color ?? "moveon.primary",
                humanReadable: primaryAction.humanReadable,
                actionDescription: primaryAction.actionDescription,
              }
            : undefined
        }
        visual={{
          uncoveredBackgroundColor: backgroundColor,
        }}
      >
        {card}
      </WithSwipeSelector>
    );

  return selectableCard;
}

export default memo(ThoughtItem);
